/**
 * Sample requests table component for shipping details
 * Displays sample requests and allows linking them to the shipping
 */

'use client';

import { FC } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { ShippingDto, SampleRequestDto } from '@/api/ApiClient';
import { KeyValueType } from '@/lib/options/optionTypes';

interface ShippingSampleRequestsTableProps {
  shipping: ShippingDto;
  visibleRequestLines: SampleRequestDto[];
  isSubmitted: boolean;
  isReadOnly: boolean;
  requestStatusOptions?: KeyValueType[];
  onRequestToggle: (requestId: number, relevant: boolean) => void;
  onPartialDeliveryChange: (requestId: number, partialDelivery: boolean) => void;
}

/**
 * Gets the display label for a request status code
 */
const getRequestStatusLabel = (statusCode: string | undefined, options?: KeyValueType[]): string => {
  if (!statusCode) return 'N/A';
  if (!options) return statusCode;
  const option = options.find((opt) => opt.key === statusCode);
  return option?.value ?? statusCode;
};

export const ShippingSampleRequestsTable: FC<ShippingSampleRequestsTableProps> = ({
  shipping,
  visibleRequestLines,
  isSubmitted,
  isReadOnly,
  requestStatusOptions,
  onRequestToggle,
  onPartialDeliveryChange,
}) => {
  if (visibleRequestLines.length === 0) {
    return null;
  }

  return (
    <Paper elevation={0} variant="outlined" className="shipping-paper">
      <Typography variant="h6" gutterBottom>
        Sample Requests
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sample Request</TableCell>
              <TableCell># requested</TableCell>
              <TableCell width={130}>Status</TableCell>
              <TableCell>In this Shipping</TableCell>
              <TableCell width={200}>Set Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRequestLines.map((request) => {
              const linkedRequest = shipping.sampleRequests?.find((r) => r.requestId === request.id);
              const isRelevant = !!linkedRequest;
              return (
                <TableRow key={request.id}>
                  <TableCell>{request.title}</TableCell>
                  <TableCell>{request.count ?? 'N/A'}</TableCell>
                  <TableCell>{getRequestStatusLabel(request.requestStatus, requestStatusOptions)}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={isRelevant}
                      disabled={isSubmitted || isReadOnly}
                      onChange={(e) => onRequestToggle(request.id!, e.target.checked)}
                      aria-label="Shipping is relevant for this request"
                    />
                  </TableCell>
                  <TableCell>
                    {isRelevant ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={linkedRequest.partialDelivery ? 'partial' : 'close'}
                          onChange={(e) => onPartialDeliveryChange(request.id!, e.target.value === 'partial')}
                          disabled={isSubmitted || isReadOnly}
                        >
                          <MenuItem value="partial">Partial Shipping</MenuItem>
                          <MenuItem value="close">Close Sample Request</MenuItem>
                        </Select>
                      </FormControl>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
